import React, { FC, HTMLAttributes, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { IconButtonWrapper, Note } from 'components/ui';
import { Field, useField } from 'react-final-form';
import { TextAreaWrapper } from 'components/forms';
import styles from './LanguageAcordionItem.module.scss';
import { t } from 'utils';

export interface ILanguageAcordionItem extends HTMLAttributes<HTMLDivElement> {
  name: string;
  languageValue: string;
  disabled?: boolean;
}

const LanguageAcordionItem: FC<ILanguageAcordionItem> = (props) => {
  const { languageValue, name, className, disabled } = props;
  const {
    input: {
      value: { description, benefits, functionality },
    },
  } = useField(name);
  const isCompleted = useMemo<boolean>(() => Boolean(description && benefits && functionality), [
    benefits,
    description,
    functionality,
  ]);
  const [hide, setHide] = useState<boolean>(true);
  const rootClassName = classNames(styles.root, className, {
    [styles.open]: !hide,
    [styles.completed]: isCompleted,
    [styles.disabled]: disabled,
  });

  const handleToggleHide = useCallback(() => setHide((prev) => !prev), []);

  return (
    <div className={rootClassName}>
      <div className={styles.header} onClick={handleToggleHide} role='presentation'>
        <p className={styles.title}>
          {t(`language.${languageValue.toLowerCase()}`)}
          <span className={styles.notCompletedText}>
            ({t('app.distribution.language.notComplete')})
          </span>
        </p>
        <IconButtonWrapper icon='chevron-down' className={styles.toggleButton} />
      </div>
      <div className={styles.content}>
        <Field
          name={`${name}.description`}
          label={t('app.distribution.language.shortDescription')}
          component={TextAreaWrapper}
          disabled={disabled}
          className='mb-1'
          required
        />
        <Note className='mb-5'>{t('app.distribution.language.shortDescription.hint')}</Note>
        <Field
          name={`${name}.benefits`}
          label={t('app.distribution.language.benefits')}
          component={TextAreaWrapper}
          disabled={disabled}
          className='mb-1'
          required
        />
        <Note className='mb-5'>{t('app.distribution.language.benefits.hint')}</Note>
        <Field
          name={`${name}.functionality`}
          label={t('app.distribution.language.features')}
          component={TextAreaWrapper}
          disabled={disabled}
          className='mb-1'
          required
        />
        <Note>{t('app.distribution.language.features.hint')}</Note>
        <Field
          name={`${name}.language`}
          initialValue={languageValue}
          component='input'
          type='hidden'
        />
      </div>
    </div>
  );
};

export default LanguageAcordionItem;
